/**
 * OpsConsole - Operations management tools
 * Phase 11.5c - Ops Console
 */
import React, { useState, useCallback, useEffect } from 'react'
import {
  IoPlay,
  IoPause,
  IoRefresh,
  IoCheckmarkCircle,
  IoCloseCircle,
  IoTime,
  IoWarning,
  IoServer,
  IoToggle,
  IoTrash,
  IoTrendingUp,
  IoBookmark,
  IoLink,
  IoArrowUp,
  IoArrowDown,
  IoSave,
  IoLockClosed,
  IoSnow,
  IoBan,
  IoAdd,
} from 'react-icons/io5'
import { apiGetAuth, apiPostAuth, apiPutAuth, apiDeleteAuth } from '../../api'
import { AccordionSection } from './AccordionSection'
import { CompactCard, CompactCardGrid } from './CompactCard'

interface JobStatus {
  job_id: string
  job_name: string
  last_run: string | null
  last_status: string | null
  last_duration_seconds: number | null
  last_error: string | null
  run_count_24h: number
  failure_count_24h: number
}

interface FeatureFlag {
  id: number
  key: string
  name: string
  description: string | null
  enabled: boolean
  rollout_percent: number
  allowed_roles: string[] | null
  allowed_user_ids: number[] | null
  created_at: string
  updated_at: string
}

interface OddsFeedHealth {
  league_id: number
  league_name: string
  country: string
  last_odds_update: string | null
  fixtures_total: number
  fixtures_with_odds: number
  fixtures_missing_odds: number
  staleness_hours: number | null
  status: 'healthy' | 'stale' | 'missing'
}

interface CacheStats {
  builder_cache: {
    total_entries: number
    expired_entries: number
    valid_entries: number
  }
  value_bets_cache: {
    total_entries: number
    expired_entries: number
    valid_entries: number
  }
}

interface BookmakerInfo {
  id: number
  api_bookmaker_id: number
  name: string
  slug: string
  logo_url: string | null
  affiliate_url_template: string | null
  is_active: boolean
  priority: number
  fixture_odds_count: number
}

interface MarketOverride {
  id: number
  fixture_id: number
  fixture_name: string | null
  market_id: number | null
  market_name: string
  override_type: 'locked' | 'voided' | 'frozen'
  line: number | null
  reason: string | null
  admin_email: string | null
  created_at: string
  expires_at: string | null
  is_active: boolean
}

interface MarketInfo {
  id: number
  name: string
  slug: string
  category: string
  has_line: boolean
  is_active: boolean
}

export function OpsConsole() {
  const [jobs, setJobs] = useState<JobStatus[]>([])
  const [flags, setFlags] = useState<FeatureFlag[]>([])
  const [oddsHealth, setOddsHealth] = useState<OddsFeedHealth[]>([])
  const [cacheStats, setCacheStats] = useState<CacheStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [healthSummary, setHealthSummary] = useState({ healthy: 0, stale: 0, missing: 0 })

  // Bookmaker state
  const [bookmakers, setBookmakers] = useState<BookmakerInfo[]>([])
  const [showInactive, setShowInactive] = useState(false)
  const [editingBookmaker, setEditingBookmaker] = useState<BookmakerInfo | null>(null)
  const [bookmakerPriorities, setBookmakerPriorities] = useState<Record<number, number>>({})
  const [prioritiesChanged, setPrioritiesChanged] = useState(false)

  // Market overrides state
  const [overrides, setOverrides] = useState<MarketOverride[]>([])
  const [markets, setMarkets] = useState<MarketInfo[]>([])
  const [showAddOverride, setShowAddOverride] = useState(false)
  const [newOverride, setNewOverride] = useState({
    fixture_id: '',
    market_id: '',
    override_type: 'locked' as 'locked' | 'voided' | 'frozen',
    line: '',
    reason: '',
  })

  // Load all data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [jobsRes, flagsRes, healthRes, cacheRes, bookmakersRes, overridesRes, marketsRes] = await Promise.all([
        apiGetAuth<{ jobs: JobStatus[] }>('/ops/scheduler/jobs'),
        apiGetAuth<{ flags: FeatureFlag[] }>('/ops/feature-flags'),
        apiGetAuth<{ leagues: OddsFeedHealth[]; summary: typeof healthSummary }>('/ops/odds-health'),
        apiGetAuth<CacheStats>('/ops/cache/stats'),
        apiGetAuth<{ bookmakers: BookmakerInfo[] }>(`/ops/bookmakers?include_inactive=${showInactive}`),
        apiGetAuth<{ overrides: MarketOverride[] }>('/ops/market-overrides?limit=50'),
        apiGetAuth<{ markets: MarketInfo[] }>('/ops/markets'),
      ])
      setJobs(jobsRes?.jobs || [])
      setFlags(flagsRes?.flags || [])
      setOddsHealth(healthRes?.leagues || [])
      setHealthSummary(healthRes?.summary || { healthy: 0, stale: 0, missing: 0 })
      setCacheStats(cacheRes || null)
      setBookmakers(bookmakersRes?.bookmakers || [])
      setOverrides(overridesRes?.overrides || [])
      setMarkets(marketsRes?.markets || [])
      // Initialize priorities
      const priorities: Record<number, number> = {}
      ;(bookmakersRes?.bookmakers || []).forEach((b: BookmakerInfo) => {
        priorities[b.id] = b.priority
      })
      setBookmakerPriorities(priorities)
      setPrioritiesChanged(false)
    } catch (error) {
      console.error('Error loading ops data:', error)
    } finally {
      setLoading(false)
    }
  }, [showInactive])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Toggle feature flag
  const toggleFlag = useCallback(async (key: string) => {
    try {
      await apiPostAuth(`/ops/feature-flags/${key}/toggle`, {})
      loadData()
    } catch (error) {
      console.error('Error toggling flag:', error)
    }
  }, [loadData])

  // Clear cache
  const clearCache = useCallback(async (type: string) => {
    try {
      await apiPostAuth(`/ops/cache/clear/${type}`, {})
      loadData()
    } catch (error) {
      console.error('Error clearing cache:', error)
    }
  }, [loadData])

  // Toggle bookmaker active status
  const toggleBookmaker = useCallback(async (id: number) => {
    try {
      await apiPostAuth(`/ops/bookmakers/${id}/toggle`, {})
      loadData()
    } catch (error) {
      console.error('Error toggling bookmaker:', error)
    }
  }, [loadData])

  // Update bookmaker priority locally
  const updateLocalPriority = useCallback((id: number, delta: number) => {
    setBookmakerPriorities(prev => {
      const newPriorities = { ...prev }
      const current = newPriorities[id] || 0
      newPriorities[id] = Math.max(0, Math.min(100, current + delta))
      return newPriorities
    })
    setPrioritiesChanged(true)
  }, [])

  // Save bookmaker priorities
  const savePriorities = useCallback(async () => {
    try {
      const priorities = Object.entries(bookmakerPriorities).map(([id, priority]) => ({
        id: parseInt(id),
        priority,
      }))
      await apiPostAuth('/ops/bookmakers/reorder', priorities)
      loadData()
    } catch (error) {
      console.error('Error saving priorities:', error)
    }
  }, [bookmakerPriorities, loadData])

  // Update bookmaker details
  const updateBookmaker = useCallback(async (id: number, data: Partial<BookmakerInfo>) => {
    try {
      await apiPutAuth(`/ops/bookmakers/${id}`, data)
      setEditingBookmaker(null)
      loadData()
    } catch (error) {
      console.error('Error updating bookmaker:', error)
    }
  }, [loadData])

  // Create market override
  const createOverride = useCallback(async () => {
    try {
      await apiPostAuth('/ops/market-overrides', {
        fixture_id: parseInt(newOverride.fixture_id),
        market_id: newOverride.market_id ? parseInt(newOverride.market_id) : null,
        override_type: newOverride.override_type,
        line: newOverride.line ? parseFloat(newOverride.line) : null,
        reason: newOverride.reason || null,
      })
      setShowAddOverride(false)
      setNewOverride({
        fixture_id: '',
        market_id: '',
        override_type: 'locked',
        line: '',
        reason: '',
      })
      loadData()
    } catch (error) {
      console.error('Error creating override:', error)
    }
  }, [newOverride, loadData])

  // Remove market override
  const removeOverride = useCallback(async (id: number) => {
    if (!confirm('Remove this market override?')) return
    try {
      await apiDeleteAuth(`/ops/market-overrides/${id}`)
      loadData()
    } catch (error) {
      console.error('Error removing override:', error)
    }
  }, [loadData])

  // Toggle market globally
  const toggleMarket = useCallback(async (id: number) => {
    try {
      await apiPostAuth(`/ops/markets/${id}/toggle`, {})
      loadData()
    } catch (error) {
      console.error('Error toggling market:', error)
    }
  }, [loadData])

  // Get override type icon
  const getOverrideIcon = (type: string) => {
    switch (type) {
      case 'locked':
        return <IoLockClosed size={14} />
      case 'frozen':
        return <IoSnow size={14} />
      case 'voided':
        return <IoBan size={14} />
      default:
        return null
    }
  }

  // Format duration
  const formatDuration = (seconds: number | null): string => {
    if (seconds === null) return '—'
    if (seconds < 1) return `${Math.round(seconds * 1000)}ms`
    if (seconds < 60) return `${seconds.toFixed(1)}s`
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`
  }

  // Get status icon
  const getStatusIcon = (status: string | null) => {
    switch (status) {
      case 'success':
        return <IoCheckmarkCircle className="status-icon success" />
      case 'failed':
        return <IoCloseCircle className="status-icon error" />
      case 'running':
        return <IoRefresh className="status-icon running spinning" />
      default:
        return <IoTime className="status-icon neutral" />
    }
  }

  return (
    <div className="ops-console">
      {/* Overview Cards */}
      <CompactCardGrid columns={4}>
        <CompactCard
          label="Scheduler Jobs"
          value={jobs.length}
          icon={<IoServer size={20} />}
          status="neutral"
        />
        <CompactCard
          label="Healthy Feeds"
          value={healthSummary.healthy}
          icon={<IoCheckmarkCircle size={20} />}
          status="healthy"
        />
        <CompactCard
          label="Stale/Missing"
          value={healthSummary.stale + healthSummary.missing}
          icon={<IoWarning size={20} />}
          status={healthSummary.stale + healthSummary.missing > 0 ? 'warning' : 'healthy'}
        />
        <CompactCard
          label="Feature Flags"
          value={`${flags.filter(f => f.enabled).length}/${flags.length}`}
          icon={<IoToggle size={20} />}
          status="neutral"
        />
      </CompactCardGrid>

      {/* Scheduler Jobs */}
      <AccordionSection
        title="Scheduler Jobs"
        badge={jobs.filter(j => j.last_status === 'failed').length || undefined}
        badgeVariant="error"
        defaultOpen={true}
      >
        <div className="ops-jobs-table">
          <div className="ops-table-header">
            <span>Job</span>
            <span>Last Run</span>
            <span>Status</span>
            <span>Duration</span>
            <span>24h Runs</span>
            <span>24h Failures</span>
          </div>
          {jobs.map((job) => (
            <div key={job.job_id} className={`ops-table-row ${job.last_status === 'failed' ? 'error' : ''}`}>
              <span className="ops-job-name">{job.job_name}</span>
              <span className="ops-job-time">
                {job.last_run ? new Date(job.last_run).toLocaleString() : '—'}
              </span>
              <span className="ops-job-status">
                {getStatusIcon(job.last_status)}
                {job.last_status || 'Never'}
              </span>
              <span className="ops-job-duration">{formatDuration(job.last_duration_seconds)}</span>
              <span className="ops-job-runs">{job.run_count_24h}</span>
              <span className={`ops-job-failures ${job.failure_count_24h > 0 ? 'has-failures' : ''}`}>
                {job.failure_count_24h}
              </span>
            </div>
          ))}
        </div>
      </AccordionSection>

      {/* Odds Feed Health */}
      <AccordionSection
        title="Odds Feed Health"
        badge={healthSummary.stale + healthSummary.missing || undefined}
        badgeVariant="warning"
        defaultOpen={false}
      >
        <div className="ops-health-table">
          <div className="ops-table-header">
            <span>League</span>
            <span>Last Update</span>
            <span>Coverage</span>
            <span>Staleness</span>
            <span>Status</span>
          </div>
          {oddsHealth.map((league) => (
            <div key={league.league_id} className={`ops-table-row ${league.status}`}>
              <span className="ops-league-name">
                {league.league_name}
                <small>{league.country}</small>
              </span>
              <span className="ops-last-update">
                {league.last_odds_update
                  ? new Date(league.last_odds_update).toLocaleString()
                  : 'Never'}
              </span>
              <span className="ops-coverage">
                {league.fixtures_with_odds}/{league.fixtures_total}
                {league.fixtures_missing_odds > 0 && (
                  <small className="missing">({league.fixtures_missing_odds} missing)</small>
                )}
              </span>
              <span className="ops-staleness">
                {league.staleness_hours !== null ? `${league.staleness_hours}h` : '—'}
              </span>
              <span className={`ops-status-badge ${league.status}`}>
                {league.status}
              </span>
            </div>
          ))}
        </div>
      </AccordionSection>

      {/* Feature Flags */}
      <AccordionSection title="Feature Flags" defaultOpen={false}>
        <div className="ops-flags-list">
          {flags.map((flag) => (
            <div key={flag.id} className={`ops-flag-item ${flag.enabled ? 'enabled' : 'disabled'}`}>
              <div className="ops-flag-info">
                <div className="ops-flag-header">
                  <span className="ops-flag-key">{flag.key}</span>
                  <span className="ops-flag-name">{flag.name}</span>
                </div>
                {flag.description && (
                  <p className="ops-flag-description">{flag.description}</p>
                )}
                <div className="ops-flag-meta">
                  <span>Rollout: {flag.rollout_percent}%</span>
                  {flag.allowed_roles && flag.allowed_roles.length > 0 && (
                    <span>Roles: {flag.allowed_roles.join(', ')}</span>
                  )}
                </div>
              </div>
              <button
                className={`ops-flag-toggle ${flag.enabled ? 'on' : 'off'}`}
                onClick={() => toggleFlag(flag.key)}
              >
                <IoToggle size={24} />
                {flag.enabled ? 'ON' : 'OFF'}
              </button>
            </div>
          ))}
          {flags.length === 0 && (
            <p className="ops-empty">No feature flags configured.</p>
          )}
        </div>
      </AccordionSection>

      {/* Cache Controls */}
      <AccordionSection title="Cache Controls" defaultOpen={false}>
        {cacheStats && (
          <div className="ops-cache-section">
            <CompactCardGrid columns={3}>
              <CompactCard
                label="Builder Cache"
                value={`${cacheStats.builder_cache.valid_entries} valid`}
                status="neutral"
                trend={`${cacheStats.builder_cache.expired_entries} expired`}
              />
              <CompactCard
                label="Value Bets Cache"
                value={`${cacheStats.value_bets_cache.valid_entries} valid`}
                status="neutral"
                trend={`${cacheStats.value_bets_cache.expired_entries} expired`}
              />
              <CompactCard
                label="Total Entries"
                value={
                  cacheStats.builder_cache.total_entries +
                  cacheStats.value_bets_cache.total_entries
                }
                status="neutral"
              />
            </CompactCardGrid>
            <div className="ops-cache-actions">
              <button
                className="ops-cache-btn"
                onClick={() => clearCache('expired')}
              >
                <IoTrash size={16} />
                Clear Expired
              </button>
              <button
                className="ops-cache-btn danger"
                onClick={() => clearCache('builder')}
              >
                <IoTrash size={16} />
                Clear Builder Cache
              </button>
              <button
                className="ops-cache-btn danger"
                onClick={() => clearCache('value_bets')}
              >
                <IoTrash size={16} />
                Clear Value Bets Cache
              </button>
              <button
                className="ops-cache-btn danger"
                onClick={() => clearCache('all')}
              >
                <IoTrash size={16} />
                Clear All Caches
              </button>
            </div>
          </div>
        )}
      </AccordionSection>

      {/* Bookmaker Management */}
      <AccordionSection
        title="Bookmaker Management"
        badge={bookmakers.filter(b => !b.is_active).length || undefined}
        badgeVariant="warning"
        defaultOpen={false}
      >
        <div className="ops-bookmakers-section">
          <div className="ops-bookmakers-header">
            <label className="ops-bookmakers-toggle">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show inactive bookmakers
            </label>
            {prioritiesChanged && (
              <button className="ops-save-priorities-btn" onClick={savePriorities}>
                <IoSave size={16} />
                Save Priority Changes
              </button>
            )}
          </div>

          <div className="ops-bookmakers-table">
            <div className="ops-bookmakers-table-header">
              <span>Bookmaker</span>
              <span>Status</span>
              <span>Priority</span>
              <span>Odds Count</span>
              <span>Affiliate</span>
              <span>Actions</span>
            </div>
            {bookmakers.map((bm) => (
              <div
                key={bm.id}
                className={`ops-bookmakers-row ${!bm.is_active ? 'inactive' : ''}`}
              >
                <span className="ops-bookmaker-name">
                  {bm.logo_url && (
                    <img src={bm.logo_url} alt="" className="ops-bookmaker-logo" />
                  )}
                  <span>{bm.name}</span>
                  <small>{bm.slug}</small>
                </span>
                <span className="ops-bookmaker-status">
                  <button
                    className={`ops-status-toggle ${bm.is_active ? 'active' : 'inactive'}`}
                    onClick={() => toggleBookmaker(bm.id)}
                  >
                    {bm.is_active ? (
                      <>
                        <IoCheckmarkCircle size={14} />
                        Active
                      </>
                    ) : (
                      <>
                        <IoCloseCircle size={14} />
                        Inactive
                      </>
                    )}
                  </button>
                </span>
                <span className="ops-bookmaker-priority">
                  <button
                    className="ops-priority-btn"
                    onClick={() => updateLocalPriority(bm.id, -5)}
                    disabled={bookmakerPriorities[bm.id] <= 0}
                  >
                    <IoArrowDown size={14} />
                  </button>
                  <span className="ops-priority-value">{bookmakerPriorities[bm.id] ?? bm.priority}</span>
                  <button
                    className="ops-priority-btn"
                    onClick={() => updateLocalPriority(bm.id, 5)}
                    disabled={bookmakerPriorities[bm.id] >= 100}
                  >
                    <IoArrowUp size={14} />
                  </button>
                </span>
                <span className="ops-bookmaker-count">
                  {bm.fixture_odds_count.toLocaleString()}
                </span>
                <span className="ops-bookmaker-affiliate">
                  {bm.affiliate_url_template ? (
                    <span className="ops-affiliate-set">
                      <IoLink size={14} />
                      Set
                    </span>
                  ) : (
                    <span className="ops-affiliate-missing">Not set</span>
                  )}
                </span>
                <span className="ops-bookmaker-actions">
                  <button
                    className="ops-edit-btn"
                    onClick={() => setEditingBookmaker(bm)}
                  >
                    Edit
                  </button>
                </span>
              </div>
            ))}
          </div>

          {/* Edit Modal */}
          {editingBookmaker && (
            <div className="ops-bookmaker-modal">
              <div className="ops-bookmaker-modal-content">
                <h5>Edit Bookmaker: {editingBookmaker.name}</h5>
                <div className="ops-bookmaker-form">
                  <label>
                    Display Name:
                    <input
                      type="text"
                      defaultValue={editingBookmaker.name}
                      id="edit-bookmaker-name"
                    />
                  </label>
                  <label>
                    Affiliate URL Template:
                    <input
                      type="text"
                      defaultValue={editingBookmaker.affiliate_url_template || ''}
                      placeholder="https://example.com/?ref={affiliate_id}"
                      id="edit-bookmaker-affiliate"
                    />
                    <small>Use {'{fixture_id}'} or {'{affiliate_id}'} as placeholders</small>
                  </label>
                  <label>
                    Logo URL:
                    <input
                      type="text"
                      defaultValue={editingBookmaker.logo_url || ''}
                      placeholder="https://example.com/logo.png"
                      id="edit-bookmaker-logo"
                    />
                  </label>
                </div>
                <div className="ops-bookmaker-modal-actions">
                  <button
                    className="ops-modal-cancel"
                    onClick={() => setEditingBookmaker(null)}
                  >
                    Cancel
                  </button>
                  <button
                    className="ops-modal-save"
                    onClick={() => {
                      const name = (document.getElementById('edit-bookmaker-name') as HTMLInputElement)?.value
                      const affiliate = (document.getElementById('edit-bookmaker-affiliate') as HTMLInputElement)?.value
                      const logo = (document.getElementById('edit-bookmaker-logo') as HTMLInputElement)?.value
                      updateBookmaker(editingBookmaker.id, {
                        name: name || undefined,
                        affiliate_url_template: affiliate || undefined,
                        logo_url: logo || undefined,
                      })
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AccordionSection>

      {/* Market Overrides */}
      <AccordionSection
        title="Market Overrides"
        badge={overrides.filter(o => o.is_active).length || undefined}
        badgeVariant="warning"
        defaultOpen={false}
      >
        <div className="ops-overrides-section">
          <div className="ops-overrides-header">
            <p className="ops-overrides-description">
              Lock, void, or freeze odds for specific fixtures and markets.
            </p>
            <button
              className="ops-add-override-btn"
              onClick={() => setShowAddOverride(true)}
            >
              <IoAdd size={16} />
              Add Override
            </button>
          </div>

          {/* Active Overrides Table */}
          <div className="ops-overrides-table">
            <div className="ops-overrides-table-header">
              <span>Fixture</span>
              <span>Market</span>
              <span>Type</span>
              <span>Line</span>
              <span>Reason</span>
              <span>Created</span>
              <span>Actions</span>
            </div>
            {overrides.length === 0 ? (
              <div className="ops-overrides-empty">No market overrides configured.</div>
            ) : (
              overrides.map((override) => (
                <div
                  key={override.id}
                  className={`ops-overrides-row ${!override.is_active ? 'inactive' : ''}`}
                >
                  <span className="ops-override-fixture">
                    {override.fixture_name || `Fixture #${override.fixture_id}`}
                  </span>
                  <span className="ops-override-market">
                    {override.market_name || 'All Markets'}
                  </span>
                  <span className={`ops-override-type ${override.override_type}`}>
                    {getOverrideIcon(override.override_type)}
                    {override.override_type}
                  </span>
                  <span className="ops-override-line">
                    {override.line !== null ? override.line : '—'}
                  </span>
                  <span className="ops-override-reason" title={override.reason || undefined}>
                    {override.reason || '—'}
                  </span>
                  <span className="ops-override-created">
                    {new Date(override.created_at).toLocaleDateString()}
                    {override.admin_email && <small>{override.admin_email}</small>}
                  </span>
                  <span className="ops-override-actions">
                    <button
                      className="ops-remove-override-btn"
                      onClick={() => removeOverride(override.id)}
                      title="Remove override"
                    >
                      <IoTrash size={14} />
                    </button>
                  </span>
                </div>
              ))
            )}
          </div>

          {/* Markets Quick Toggle */}
          <div className="ops-markets-quick-section">
            <h5>Quick Market Toggles</h5>
            <div className="ops-markets-grid">
              {markets.slice(0, 12).map((market) => (
                <button
                  key={market.id}
                  className={`ops-market-toggle-btn ${market.is_active ? 'active' : 'inactive'}`}
                  onClick={() => toggleMarket(market.id)}
                  title={market.name}
                >
                  <span className="ops-market-name">{market.name}</span>
                  <span className={`ops-market-status ${market.is_active ? 'on' : 'off'}`}>
                    {market.is_active ? 'ON' : 'OFF'}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Add Override Modal */}
          {showAddOverride && (
            <div className="ops-override-modal">
              <div className="ops-override-modal-content">
                <h5>Add Market Override</h5>
                <div className="ops-override-form">
                  <label>
                    Fixture ID:
                    <input
                      type="number"
                      value={newOverride.fixture_id}
                      onChange={(e) =>
                        setNewOverride({ ...newOverride, fixture_id: e.target.value })
                      }
                      placeholder="Enter fixture ID"
                    />
                  </label>
                  <label>
                    Market (optional):
                    <select
                      value={newOverride.market_id}
                      onChange={(e) =>
                        setNewOverride({ ...newOverride, market_id: e.target.value })
                      }
                    >
                      <option value="">All Markets</option>
                      {markets.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Override Type:
                    <select
                      value={newOverride.override_type}
                      onChange={(e) =>
                        setNewOverride({
                          ...newOverride,
                          override_type: e.target.value as 'locked' | 'voided' | 'frozen',
                        })
                      }
                    >
                      <option value="locked">Locked - Hide from display</option>
                      <option value="frozen">Frozen - Freeze current odds</option>
                      <option value="voided">Voided - Mark as void</option>
                    </select>
                  </label>
                  <label>
                    Line (optional):
                    <input
                      type="number"
                      step="0.5"
                      value={newOverride.line}
                      onChange={(e) =>
                        setNewOverride({ ...newOverride, line: e.target.value })
                      }
                      placeholder="e.g., 2.5 for Over/Under"
                    />
                  </label>
                  <label>
                    Reason:
                    <textarea
                      value={newOverride.reason}
                      onChange={(e) =>
                        setNewOverride({ ...newOverride, reason: e.target.value })
                      }
                      placeholder="Why is this override being applied?"
                      rows={3}
                    />
                  </label>
                </div>
                <div className="ops-override-modal-actions">
                  <button
                    className="ops-modal-cancel"
                    onClick={() => setShowAddOverride(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="ops-modal-save"
                    onClick={createOverride}
                    disabled={!newOverride.fixture_id}
                  >
                    Create Override
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </AccordionSection>
    </div>
  )
}
